/**
 * Instructor framework synchronous constants (G2-01, HG-2A).
 *
 * The single in-code copy of the IEC criteria and the 45 Instructor
 * Observable Behaviours (WingMentor Core Framework sections 3.1-3.5).
 * Previously duplicated four times (api/lib/prompt-templates.ts,
 * api/lib/instructor-assessment/iob-mapper.ts,
 * frontend/src/types/instructor-assessment-export.ts,
 * mobile/src/types/assessment.ts); every copy now imports from here.
 *
 * The instructor framework has no registry tables yet (migrations 232/233
 * cover the pilot framework only), so this module stays the source of
 * record in code. The instructor_iob_codes lookup table is reconciled to
 * exactly this content by the G2-01 migration.
 *
 * Two description layers per behaviour:
 *  - description: the base wording every display surface shows.
 *  - developmentLevelGuidance (six calibrated codes): the
 *    development-level-calibrated addendum the LLM prompts carry.
 * getIobPromptDescription() joins the two and reproduces the historical
 * prompt strings byte for byte.
 */

export const IEC_CRITERIA = ['IEC1', 'IEC2', 'IEC3', 'IEC4', 'IEC5'] as const;

export type IECCriterion = (typeof IEC_CRITERIA)[number];

export const IEC_FULL_NAMES: Record<IECCriterion, string> = {
  IEC1: 'Pilot competencies',
  IEC2: 'Management of the learning environment',
  IEC3: 'Instruction',
  IEC4: 'Interaction',
  IEC5: 'Assessment and evaluation',
};

export const IEC_DESCRIPTIONS: Record<IECCriterion, string> = {
  IEC1: 'Demonstrates mastery of pilot competencies, applies TEM and CBTA/EBT methodology',
  IEC2: 'Creates positive, safe learning environment conducive to growth',
  IEC3: 'Delivers clear, structured, development-level-calibrated instruction; facilitation proportion matches the trainee development level',
  IEC4: 'Engages in effective facilitative communication and encourages self-assessment',
  IEC5: 'Provides fair, evidence-based assessment aligned with observable behaviors',
};

/** One Instructor Observable Behaviour (IOB). */
export interface InstructorObservableBehaviour {
  code: string;
  description: string;
}

/**
 * Complete IOB definitions per IEC criterion (45 total), base wording.
 * Format: IEC-<criterion>.<index> (e.g. IEC-1.1, IEC-3.11).
 */
export const IOB_DEFINITIONS: Record<IECCriterion, InstructorObservableBehaviour[]> = {
  IEC1: [
    { code: 'IEC-1.1', description: 'Demonstrates mastery of ICAO Pilot Core Competencies (KNO, PRO, COM, FPA, FPM, LTW, PSD, SAW, WLM).' },
    { code: 'IEC-1.2', description: 'Applies pilot competencies effectively to instruction, facilitation, and assessment.' },
    { code: 'IEC-1.3', description: 'Models safe, competent, and standardised operational behaviours during instruction.' },
    { code: 'IEC-1.4', description: 'Integrates TEM and operational realism into training demonstrations.' },
  ],
  IEC2: [
    { code: 'IEC-2.1', description: 'Applies TEM in the context of instruction/evaluation.' },
    { code: 'IEC-2.2', description: 'Briefs on safety procedures for situations likely to develop during instruction/evaluation.' },
    { code: 'IEC-2.3', description: 'Intervenes appropriately at the correct time and level (progressing from verbal guidance to taking over control).' },
    { code: 'IEC-2.4', description: 'Resumes training/evaluation as practicable after any intervention.' },
    { code: 'IEC-2.5', description: 'Plans and prepares training media, equipment, and resources.' },
    { code: 'IEC-2.6', description: 'Briefs training device or aircraft limitations that may influence training.' },
    { code: 'IEC-2.7', description: 'Creates and manages conditions suitable for the training objectives (FSTD, ATC, weather, time, etc.).' },
    { code: 'IEC-2.8', description: 'Adapts to changes in the environment while minimising disruptions.' },
    { code: 'IEC-2.9', description: 'Manages time, training media, and equipment effectively.' },
  ],
  IEC3: [
    { code: 'IEC-3.1', description: 'References approved operations, technical, and training manuals, standards, and regulations.' },
    { code: 'IEC-3.2', description: 'States clearly the objectives and clarifies trainee and instructor roles.' },
    { code: 'IEC-3.3', description: 'Follows the approved training programme and syllabus.' },
    { code: 'IEC-3.4', description: 'Applies instructional methods appropriately (explanation, demonstration, facilitation, guided discovery, in-seat instruction).' },
    { code: 'IEC-3.5', description: 'Maintains operational realism and relevance throughout training.' },
    { code: 'IEC-3.6', description: 'Adapts instructor input to ensure objectives are achieved.' },
    { code: 'IEC-3.7', description: 'Adapts to unplanned situations effectively.' },
    { code: 'IEC-3.8', description: 'Continuously assesses trainee competencies and progress.' },
    { code: 'IEC-3.9', description: 'Encourages trainee self-assessment.' },
    { code: 'IEC-3.10', description: 'Allows trainee to self-correct appropriately.' },
    { code: 'IEC-3.11', description: 'Applies trainee-centred feedback and facilitation techniques.' },
    { code: 'IEC-3.12', description: 'Provides positive reinforcement.' },
  ],
  IEC4: [
    { code: 'IEC-4.1', description: 'Shows respect for trainee culture, language, and experience.' },
    { code: 'IEC-4.2', description: 'Demonstrates patience, empathy, and active listening.' },
    { code: 'IEC-4.3', description: 'Manages barriers to learning such as fear of error or low confidence.' },
    { code: 'IEC-4.4', description: 'Encourages engagement, teamwork, and mutual support.' },
    { code: 'IEC-4.5', description: 'Coaches trainees to enhance self-awareness and performance.' },
    { code: 'IEC-4.6', description: 'Supports organisational training goals and authority policies.' },
    { code: 'IEC-4.7', description: 'Demonstrates integrity, honesty, and professionalism.' },
    { code: 'IEC-4.8', description: 'Acts as a model for acceptable professional conduct and behaviour.' },
    { code: 'IEC-4.9', description: 'Actively seeks and accepts feedback to improve performance.' },
  ],
  IEC5: [
    { code: 'IEC-5.1', description: 'Complies with Operator/ATO and authority requirements.' },
    { code: 'IEC-5.2', description: 'Ensures trainee understanding of the assessment process.' },
    { code: 'IEC-5.3', description: 'Applies competency standards and conditions.' },
    { code: 'IEC-5.4', description: 'Assesses trainee competencies objectively.' },
    { code: 'IEC-5.5', description: 'Performs grading based on evidence, not personal preference.' },
    { code: 'IEC-5.6', description: 'Provides recommendations and decisions based on assessment outcomes.' },
    { code: 'IEC-5.7', description: 'Provides clear feedback to trainees.' },
    { code: 'IEC-5.8', description: 'Reports strengths and weaknesses of the training system including trainee feedback.' },
    { code: 'IEC-5.9', description: 'Suggests improvements for the training system.' },
    { code: 'IEC-5.10', description: 'Records grades and evidence accurately using appropriate media.' },
    { code: 'IEC-5.11', description: 'Participates in calibration and standardisation sessions.' },
  ],
};

/**
 * Development-level-calibrated guidance for the six calibrated IOBs.
 * Vocabulary per HG-1B (GCH-08/GCH-09): "development level" replaces the
 * withdrawn "tier"; level names Beginner / Building Competencies /
 * Proficient and the 70/30, 40/60, 10/90 proportions are unchanged.
 */
export const IOB_DEVELOPMENT_LEVEL_GUIDANCE: Record<string, string> = {
  'IEC-3.4':
    "Development-level-calibrated proportions: Beginner expects ~70% directive instruction / 30% facilitation with frequent demonstrations and Objective/Procedural questioning; Building Competencies expects ~40/60 with Reflective/Competency-Linking questioning and occasional demonstration; Proficient expects ~10/90 with Developmental/Competency-Linking questioning and rare demonstration. NOT 'facilitation over instruction' in absolute terms.",
  'IEC-3.9':
    'Always invited, but content scales by development level: Beginner needs structured prompts (cannot reliably self-assess); Building Competencies should self-identify what happened but needs help with root cause; Proficient should self-identify both. Expecting open-ended self-assessment from a Beginner who cannot answer is itself a deficiency.',
  'IEC-3.10':
    'Development-level-calibrated: Beginner needs prompt directive correction (let them self-correct only on items already mastered); Building Competencies should be given a few seconds to attempt before guidance; Proficient should be allowed to self-correct unless safety-critical.',
  'IEC-3.11':
    "Delivery form scales by development level: Beginner, direct evaluation statement first, then guided question for understanding; Building Competencies, guided question, with a statement only when the trainee cannot self-identify; Proficient, confirmation or challenge framed around the trainee's own assessment. Direct statements to a Proficient trainee or pure open-ended questioning to a Beginner who cannot self-assess are development level mismatch deficiencies.",
  'IEC-4.5':
    'Coaching depth scales by development level: Beginner, point out what to notice; Building Competencies, invite reflection on what they noticed; Proficient, challenge the inference and root cause. Skipping the lower steps with a Beginner is over-coaching; staying at the surface with a Proficient is under-coaching.',
  'IEC-5.7':
    'Development-level-calibrated form: Beginner, direct statement of what was correct or incorrect with rationale; Building Competencies, guided question that surfaces the gap; Proficient, confirmation or challenge framed around the trainee\'s own assessment.',
};

/**
 * Prompt-facing description for an IOB: base wording plus the
 * development-level guidance where the code carries one. Reproduces the
 * historical INSTRUCTOR_IEC_CODES strings byte for byte.
 */
export function getIobPromptDescription(iob: InstructorObservableBehaviour): string {
  const guidance = IOB_DEVELOPMENT_LEVEL_GUIDANCE[iob.code];
  return guidance ? `${iob.description} ${guidance}` : iob.description;
}

/**
 * The canonical IOB code shape, migration 244's format: IEC-<criterion>.<index>.
 * Tolerated inputs are the hyphenless and space-separated drifts an LLM emits,
 * in any case: "IEC2.3", "iec 2.3", "IEC-2.3".
 */
const IOB_CODE_SHAPE = /^IEC[-\s]?([1-5])\.(\d{1,2})$/i;

/**
 * Canonicalise one IOB code, or return null when the string is not an IOB code
 * at all.
 *
 * Why this exists: the 18 August A/B run recorded six codes emitted as
 * "IEC2.3" style beside canonical siblings in the same response
 * (HG-4A-AB-REGRESSION.md section 7.4). Nothing downstream tolerated the
 * drift. getIOBByCode compares strings, so those six resolved to no behaviour
 * and were dropped from the evidence; computeObservedFacilitationBalance
 * matches against hyphenated sets, so they fell out of the facilitation
 * balance as well. The emission is the model's, and the prompt already carries
 * the canonical examples, so the repair belongs at the boundary that reads the
 * model's output and not in the prompt text, which would also invalidate the
 * G4-02 golden strings.
 *
 * Format only: this normalises the shape, and does NOT assert that the code
 * exists in IOB_DEFINITIONS. Membership stays the caller's decision, which is
 * what lets a storage boundary keep an unknown code visible rather than
 * silently discarding it.
 */
export function normalizeIobCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;

  const match = raw.trim().match(IOB_CODE_SHAPE);
  if (!match) return null;

  return `IEC-${match[1]}.${match[2]}`;
}

/** Look up an IOB's base description by code across all criteria. */
export function getIobDescription(code: string): string {
  for (const criterion of IEC_CRITERIA) {
    const found = IOB_DEFINITIONS[criterion].find((iob) => iob.code === code);
    if (found) return found.description;
  }
  return '';
}
